/**
 * JuggleState — pure juggle-arc data and helpers.
 *
 * No Phaser, no Combat imports — fully testable in node.
 *
 * A juggle begins when a launcher HitSpec connects on a grounded target.
 * The target rises under JUGGLE_LAUNCH_VY then falls under JUGGLE_GRAVITY.
 * Follow-up aerial hits re-boost the arc with diminishing hitstun; a hard
 * hit-cap (JUGGLE_MAX_HITS) zeroes out further stun to prevent infinites.
 */

/** Initial upward velocity applied by a launcher hit (px/frame). */
export const JUGGLE_LAUNCH_VY = 8;

/** Downward acceleration per frame (px/frame²). */
export const JUGGLE_GRAVITY = 0.5;

/** Maximum aerial hits before hitstun is zeroed (infinite-prevention cap). */
export const JUGGLE_MAX_HITS = 6;

/** Hitstun granted for the first aerial follow-up (frames). */
export const JUGGLE_HITSTUN_BASE = 18;

/** Hitstun reduction applied for each successive aerial hit (frames). */
export const JUGGLE_HITSTUN_DECAY = 3;

/** Minimum hitstun granted regardless of decay (frames). */
export const JUGGLE_HITSTUN_MIN = 4;

/** Hitstun applied to the target when the juggle arc lands (frames). */
export const JUGGLE_LANDING_HITSTUN = 25;

/** Upward re-boost applied to vy on each aerial hit (px/frame); decreases per hit. */
export const JUGGLE_REBOOST_BASE_VY = 5;

/** Mutable per-target juggle arc state. */
export interface JuggleState {
  elevation: number;
  vy: number;
  /** How many aerial hits have connected so far. */
  hitCount: number;
}

/** Create initial juggle state immediately after a launcher connects. */
export function beginJuggle(): JuggleState {
  return { elevation: 1, vy: JUGGLE_LAUNCH_VY, hitCount: 0 };
}

/**
 * Advance the juggle arc one frame. Mutates state in place.
 * @returns true while the target is still airborne; false on landing.
 */
export function tickJuggle(state: JuggleState): boolean {
  state.vy -= JUGGLE_GRAVITY;
  state.elevation += state.vy;
  if (state.elevation <= 0) {
    state.elevation = 0;
    state.vy = 0;
    return false;
  }
  return true;
}

/** Whether another aerial hit is permitted (below the cap). */
export function juggleHitAllowed(state: JuggleState): boolean {
  return state.hitCount < JUGGLE_MAX_HITS;
}

/**
 * Compute effective aerial hitstun for the next hit, with diminishing returns.
 * Returns 0 when the cap has been reached.
 */
export function juggleHitstun(state: JuggleState): number {
  if (state.hitCount >= JUGGLE_MAX_HITS) return 0;
  const decay = state.hitCount * JUGGLE_HITSTUN_DECAY;
  return Math.max(JUGGLE_HITSTUN_MIN, JUGGLE_HITSTUN_BASE - decay);
}

/**
 * Record an aerial hit and re-boost the target upward.
 * Must be called after juggleHitAllowed() returns true.
 * Mutates state.
 */
export function applyJuggleHit(state: JuggleState): void {
  state.hitCount += 1;
  const boost = Math.max(1, JUGGLE_REBOOST_BASE_VY - (state.hitCount - 1) * 0.8);
  state.vy = boost;
}
